import { isIOSNative } from '@xunlei/universal-native-api'
import AndroidBriage from './android-briage'
import { jsonPare, noop } from '../base'

import { IUserInfo } from 'types/user'
import { ITargetPathItem } from 'types/bt'

export * from './device'
export * from './disk'
export * from './mqtt'
export * from './job'
export * from './bt'
export * from './storage'

export const isHeziNative = navigator.userAgent.includes('Hezi')
export const isAndroidHezi = isHeziNative && (typeof window.HeZi === 'object' && window.HeZi)
export const isIOSHezi = isHeziNative && isIOSNative

export function closeActivity() {
    if (isIOSNative) {
        const $native = window.WebViewJavascriptBridge
        $native.callHandler('xlCloseView')
        return true
    } else if (isAndroidHezi) {
        const $native = window.HeZi
        $native.finish()
        return true
    } else {
        console.error('请在迅雷盒子客户端下操作')
        return false
    }
}
interface INativeUserInfoResult extends IUserInfo {
    appId: number
    appVersion: string
    authorize: string
    buildVersion: string
    /** 手机设备id */
    deviceId: string
    isVip: boolean
    nickName: string
    peerId: string
    phoneNumber: string
    sessionId: string
    userId: number
    userName: string
    vipLevel: number
    vipType: number
    expireDate: string
    hzInfo: {
        vip: {
            expire: number
            expire_time: string
            isvip: boolean
            pay_url:string
            svip_pay_url:string
        }
    }
}

export const getUserInfo = (): Promise<IUserInfo> => {
    if (!isIOSNative && !AndroidBriage.isAndroidNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }
    const funcName = isIOSNative ? 'userInfo' : 'getUserInfo'
    const $native = window.WebViewJavascriptBridge || AndroidBriage
    return new Promise((resolve, reject) => {
        $native.callHandler<INativeUserInfoResult>(funcName, (err, result) => {
            if (err) {
                reject(err)
                return
            }
            console.log('result', result)
            const { userId, authorize, nickName, expireDate, hzInfo, isVip, isYear, payId, isAutoDeduct, vasType, vipType } = result
            resolve({
                userid: String(userId),
                nickname: nickName,
                access_token: authorize,
                expireDate: expireDate,
                hzInfo,
                vasType: vasType || vipType,
                isVip, isYear, payId, isAutoDeduct
            })
            return
        })
    })
}

export const getAppVersion = (): Promise<string> => {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }
    const $native = window.WebViewJavascriptBridge
    return new Promise((resolve, reject) => {
        $native.callHandler<{ version: string }>('appVersion', (err, result) => {
            if (err) {
                reject(err)
                return
            }
            const { version } = result
            resolve(version)
            return
        })
    })
}

export const getAppVersionFromUA = () => {
    const ua = navigator.userAgent
    if (/Hezi\/([^\s]+)/.test(ua)) {
        return RegExp.$1
    }
    return ''
}

export const getAppBuildCode = (): Promise<string> => {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }

    const $native = window.WebViewJavascriptBridge
    return new Promise((resolve, reject) => {
        $native.callHandler<{ buildVersion: string }>('userInfo', (err, result) => {
            if (err) {
                reject(err)
                return
            }
            const { buildVersion } = result
            resolve(buildVersion)
            return
        })
    })
}

// 获取剪切板内容
export const getClipboard = (): Promise<{ content: string }> => {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }
    const $native = window.WebViewJavascriptBridge
    return new Promise((resolve, reject) => {
        $native.callHandler<{ content: string }>('fetchClipboard', (err, result) => {
            if (err) {
                reject(err)
                return
            }
            resolve(result)
            return
        })
    })
}

// 监听页面是退至后台或者从后台推至前台
export const onVisibilitychange = (callback?: Function) => {
    const visibilitychange = function () {
        callback && callback()
    }
    return document.addEventListener('visibilitychange', visibilitychange, false)
}

// 监听剪切板
type TOnClipboardCallback = (event: { content: string }) => void
export const onClipboard = (callback: TOnClipboardCallback) => {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }

    const autoGetClipboard = () => getClipboard().then(event => {
        const { content } = event
        // 复制的消息可能来自于不同操作系统，从mac QQ发送到iOS QQ，再复制到iOS 迅雷盒子的消息中，以回车结尾
        // 在此统一转化为换行符（TextArea输入框中已自动处理为换行符）
        const evt = { content: content.replace(/\r/g, '\n') }
        return callback(evt)
    })
    autoGetClipboard()
    return onVisibilitychange(autoGetClipboard)
}

/** 当前设备是否是刘海屏 */
export function isIPhoneX(): Promise<boolean> {
    if (!isIOSNative) return Promise.reject(new Error('请先安装客户端'))

    return new Promise((resolve, reject) => {
        const $native = window.WebViewJavascriptBridge
        $native.checkFunction('isIPhoneX', ({ exist }) => {
            if (!exist) reject(new Error('暂不支持该方法(isIPhoneX)'))
            $native.callHandler('isIPhoneX', (err, iPhoneX) => {
                if (err) {
                    reject(err)
                    return
                }
                resolve(iPhoneX)
            })
        })
    })
}

export enum EThemeMode {
    default = 'default',
    dark = 'dark'
}

export type TThemeMode = { mode: keyof typeof EThemeMode }
// 设置主题色
export const setTheme = (mode: TThemeMode['mode']) => {
    document.body.setAttribute('data-theme', mode)
}

// 获取主题色
export const getTheme = (): Promise<'default' | 'dark'> => {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }
    const $native = window.WebViewJavascriptBridge
    return new Promise((resolve, reject) => {
        $native.callHandler<TThemeMode>('getTheme', (err, result) => {
            if (err) {
                reject(err)
                return
            }
            resolve(result.mode)
            return
        })
    })
    // return (document.body.getAttribute('data-theme') || 'default') as 'default' | 'dark'
}

export const onThemeChange = (observer: (model: TThemeMode['mode']) => void) => {
    if (!isIOSNative) return noop
    const $native = window.WebViewJavascriptBridge
    const listenerId = $native.addEventListener('On_Theme_Change', function (result: TThemeMode) {
        observer(result.mode)
    })

    getTheme().then(setTheme)

    return () => {
        $native.removeEventListener('On_Theme_Change', listenerId)
    }
}

// 跳转至网络设置页
export const goNetworkSetting = () => {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }
    const $native = window.WebViewJavascriptBridge
    $native.callHandler<void>('goNetworkSetting')
}

export interface INativeToastOptions {
    /** 带跳转功能的toast，action为跳转的目标页面，目前有: gotoCloudFolder */
    action?: string
    /** 当action为gotoCloudFolder时，id默认不传则跳转至我的转存，若传递id，则跳转至对应目录 */
    id?: string
}

export function showNativeToast(message: string, options?: INativeToastOptions, callback?: (err: any, result: any) => void) {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }
    const params = options ? { message, ...options } : { message }
    const $native = window.WebViewJavascriptBridge
    $native.callHandler('toastMessage', params, callback)
}

export function notifyNativeHVipActivationSucceeded() {
    // 通知原生领取完成
    if (!isIOSNative && !AndroidBriage.isAndroidNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }

    if (AndroidBriage.isAndroidNative) {
        AndroidBriage.callHandler('setActivityResult', null, JSON.stringify({ code: 0 }))
    } else {
        window.WebViewJavascriptBridge.callHandler('hasActivateBoxVip')
    }
}

// 跳转原生续费
export function openVipPurchase(payUrl?: string) {
    if (!isIOSNative && !AndroidBriage.isAndroidNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }

    if (AndroidBriage.isAndroidNative) {
        // 盒子安卓打开续费支付页
        window.HeZi?.callByJson(JSON.stringify(
            {
                router: 'web/h5',
                params: {
                    url: payUrl || 'https://pay-hezi.xunlei.com/pages/2020/android-sl-pay/?frompage=hygw&referfrom=v_pc_hygw_ggong_dhl_info&platform=default&aidfrom=hygw_dhl_kt&status_bar=false&toolbar=false',
                    "enable_js": true,
                    "add_cookie": true,
                    "title": "盒子会员支付"
                }
            }
        ))
        window?.HeZi?.finish()
    } else {
        window.WebViewJavascriptBridge.callHandler('openVipPurchase', { referfrom: 'v_ios_xos_ggong_acti_str_svip', aidfrom: '' })
    }
}

interface IOpenHeziPathSelectorOptions {
    initPath: string
}
interface IPathSelectorResult {
    // -1: 用户取消选择; 0: 确认选择
    ret: -1 | 0
    data: ITargetPathItem
}
/**
 * 调用客户端选择盒子路径面板，异步返回选择结果
 * @param options 见IOpenHeziPathSelectorOptions
 * @returns 
 */
export function openHeziPathSelector(options: IOpenHeziPathSelectorOptions): Promise<IPathSelectorResult> {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }

    const $native = window.WebViewJavascriptBridge
    return new Promise((resolve, reject) => {
        $native.checkFunction('openHeziPathSelector', (result) => {
            if (!result.exist) {
                reject(new Error('暂不支持该方法调用(openHeziPathSelector)'))
                return
            }
            $native.callHandler<IPathSelectorResult>('openHeziPathSelector', options, (err, response) => {
                if (err) {
                    reject(err)
                    return
                }

                const { ret, data: result } = response
                const data = result && typeof result === 'string' ? jsonPare(result) as ITargetPathItem : result

                resolve({ ret, data })
                return
            })
        })
    })
}

interface IOpenYunPanPathSelectorOptions {
    /** 父文件夹id */
    parent_id: string
}
/**
 * 调用客户端选择云盘路径面板，异步返回选择结果
 * @param options IOpenYunPanPathSelectorOptions
 * @returns 
 */
export function openYunPanPathSelector(options: IOpenYunPanPathSelectorOptions): Promise<IPathSelectorResult> {
    if (!isIOSNative) {
        return Promise.reject('请在迅雷盒子客户端下操作')
    }

    const $native = window.WebViewJavascriptBridge
    return new Promise((resolve, reject) => {
        $native.checkFunction('openYunPanPathSelector', (result) => {
            if (!result.exist) {
                reject(new Error('暂不支持该方法调用(openYunPanPathSelector)'))
                return
            }
            $native.callHandler<IPathSelectorResult>('openYunPanPathSelector', options, (err, response) => {
                if (err) {
                    reject(err)
                    return
                }
                const { ret, data: result } = response
                const data = result && typeof result === 'string' ? jsonPare(result) as ITargetPathItem : result

                resolve({ ret, data })
                return
            })
        })
    })
}
